/**
 * Driver Rewards Page - XP, Levels, Badges, Streaks
 * 3D visual elements with  branding
 */
import { useState, useEffect, Suspense, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, RoundedBox, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Star,
  Trophy,
  Flame,
  Zap,
  Gift,
  TrendingUp,
  Medal,
  Target,
  Share2,
  Copy,
  ChevronRight,
  Crown,
  Award,
  Clock
} from 'lucide-react';

// Jamaican Color Palette
const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
  paleGreen: '#E8FFF4',
};

// ============== 3D COMPONENTS ==============

// 3D Badge Medal
function BadgeMedal3D({ position, color, earned = false }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current && earned) {
      meshRef.current.rotation.y += 0.01;
    }
  });

  return (
    <Float speed={earned ? 2 : 0} rotationIntensity={0} floatIntensity={earned ? 0.3 : 0}>
      <group ref={meshRef} position={position}>
        {/* Medal base */}
        <mesh>
          <cylinderGeometry args={[0.4, 0.4, 0.08, 32]} />
          <meshStandardMaterial 
            color={earned ? color : '#4a5568'}
            metalness={earned ? 0.9 : 0.3}
            roughness={0.2}
          />
        </mesh>
        
        {/* Star emboss */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.25, 0.25, 0.02, 5]} />
          <meshStandardMaterial 
            color={earned ? COLORS.white : '#718096'}
            metalness={0.5}
            roughness={0.3}
          />
        </mesh>
        
        {/* Ribbon */}
        <mesh position={[0, 0.35, 0]}>
          <boxGeometry args={[0.15, 0.5, 0.02]} />
          <meshStandardMaterial color={earned ? COLORS.green : '#4a5568'} />
        </mesh>
      </group>
    </Float>
  );
}

// 3D Coin
function Coin3D({ position, spinning = true }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current && spinning) {
      meshRef.current.rotation.y += 0.03;
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.1} floatIntensity={0.4}>
      <group ref={meshRef} position={position}>
        <mesh>
          <cylinderGeometry args={[0.3, 0.3, 0.05, 32]} />
          <meshStandardMaterial 
            color={COLORS.yellow}
            metalness={0.95}
            roughness={0.1}
          />
        </mesh>
        <mesh position={[0, 0.03, 0]}>
          <cylinderGeometry args={[0.2, 0.2, 0.02, 6]} />
          <meshStandardMaterial 
            color={COLORS.green}
            metalness={0.7}
            roughness={0.2}
          />
        </mesh>
      </group>
    </Float>
  );
}

// 3D Star
function Star3D({ position, color = COLORS.yellow }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 2) * 0.1;
      meshRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 3) * 0.05);
    }
  });

  return (
    <Float speed={3} rotationIntensity={0.2} floatIntensity={0.5}>
      <group ref={meshRef} position={position}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.25, 0.25, 0.08, 5]} />
          <meshStandardMaterial 
            color={color}
            metalness={0.8}
            roughness={0.2}
            emissive={color}
            emissiveIntensity={0.2}
          />
        </mesh>
      </group>
    </Float>
  );
}

// Hero 3D Scene
function HeroScene3D({ level, xp }) {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 10, 5]} intensity={1} />
      <pointLight position={[-3, 3, 2]} intensity={0.5} color={COLORS.green} />
      <pointLight position={[3, 3, 2]} intensity={0.5} color={COLORS.yellow} />
      
      {/* Central Badge */}
      <BadgeMedal3D position={[0, 0, 0]} color={COLORS.yellow} earned={true} />
      
      {/* Orbiting stars */}
      <Star3D position={[-1.5, 0.5, 0]} color={COLORS.green} />
      <Star3D position={[1.5, 0.3, 0]} color={COLORS.yellow} />
      <Coin3D position={[-1, -0.5, 0.5]} />
      <Coin3D position={[1.2, -0.3, 0.3]} />
      
      <Environment preset="city" />
    </>
  );
}

// ============== UI COMPONENTS ==============

// XP Progress Bar
function XPProgressBar({ current, max, level }) {
  const percentage = max > 0 ? (current / max) * 100 : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center font-bold text-black shadow-lg shadow-yellow-500/30">
            {level}
          </div>
          <div>
            <p className="text-sm text-slate-400">Level {level}</p>
            <p className="text-white font-semibold">Driver</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-yellow-400">{current.toLocaleString()} XP</p>
          <p className="text-xs text-slate-400">/ {max.toLocaleString()} XP to Level {level + 1}</p>
        </div>
      </div>
      
      <div className="relative h-4 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 via-emerald-400 to-green-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 animate-pulse" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-white drop-shadow">{percentage.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}

// Streak Display
function StreakDisplay({ days, isActive }) {
  return (
    <Card className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isActive ? 'bg-gradient-to-br from-orange-400 to-red-500 animate-pulse' : 'bg-slate-700'}`}>
            <Flame className={`w-8 h-8 ${isActive ? 'text-white' : 'text-slate-500'}`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{days} Days</p>
            <p className="text-sm text-orange-300">{isActive ? '🔥 Streak Active!' : 'Start your streak!'}</p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-xs text-slate-400">Next Bonus</p>
          <p className="text-lg font-bold text-orange-400">
            {days < 3 ? `${3 - days} days → J$200` : 
             days < 7 ? `${7 - days} days → J$600` :
             days < 14 ? `${14 - days} days → J$1,200` :
             days < 30 ? `${30 - days} days → J$3,000` : 'Max!'}
          </p>
        </div>
      </div>
    </Card>
  );
}

// Badge Card
function BadgeCard({ badge }) {
  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-slate-400 to-slate-600',
    gold: 'from-yellow-400 to-amber-500',
    platinum: 'from-purple-400 to-indigo-500'
  };
  
  return (
    <div className={`relative p-4 rounded-xl border ${badge.earned ? 'bg-slate-800/80 border-green-500/50' : 'bg-slate-900/50 border-slate-700/50 opacity-60'}`}>
      {badge.earned && (
        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      
      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tierColors[badge.tier] || tierColors.bronze} flex items-center justify-center mb-3 shadow-lg`}>
        <span className="text-2xl">{badge.icon}</span>
      </div>
      
      <h4 className="font-semibold text-white mb-1">{badge.name}</h4>
      <p className="text-xs text-slate-400 mb-2">{badge.description}</p>
      
      {badge.xp_reward > 0 && (
        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
          +{badge.xp_reward} XP
        </Badge>
      )}
    </div>
  );
}

// Reward History Item
function RewardItem({ reward }) {
  const getIcon = () => {
    switch(reward.reward_type) {
      case 'xp': return <Zap className="w-5 h-5 text-yellow-400" />;
      case 'cash': return <Gift className="w-5 h-5 text-green-400" />;
      case 'badge': return <Medal className="w-5 h-5 text-purple-400" />;
      default: return <Star className="w-5 h-5 text-amber-400" />;
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
        {getIcon()}
      </div>
      <div className="flex-1">
        <p className="text-sm text-white">{reward.description}</p>
        <p className="text-xs text-slate-400">
          {new Date(reward.timestamp).toLocaleDateString()}
        </p>
      </div>
      <div className="text-right">
        {reward.xp_earned > 0 && <p className="text-sm text-yellow-400">+{reward.xp_earned} XP</p>}
        {reward.amount > 0 && <p className="text-sm text-green-400">+J${reward.amount}</p>}
      </div>
    </div>
  );
}

// ============== MAIN COMPONENT ==============

export default function DriverRewards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [state, setState] = useState(null);
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stateRes, badgesRes, rewardsRes, referralRes] = await Promise.all([
        api.get('/gamification/my-state?role=driver'),
        api.get('/gamification/my-badges?role=driver'),
        api.get('/gamification/my-rewards?role=driver&limit=10'),
        api.get('/gamification/referral-code?role=driver')
      ]);
      
      setState(stateRes.data);
      setBadges(badgesRes.data.badges || []);
      setRewards(rewardsRes.data.rewards || []);
      setReferralCode(referralRes.data.referral_code || '');
    } catch (error) {
      console.error('Error fetching gamification data:', error);
      toast.error('Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading rewards...</p>
        </div>
      </div>
    );
  }

  const xpProgress = state?.xp_progress || {};
  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/driver')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Driver Rewards
                </h1>
                <p className="text-sm text-slate-400">
                  Level {state?.level || 1} Driver
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* 3D Hero Section */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* 3D Scene */}
            <div className="h-48 md:h-auto md:w-1/3 bg-slate-900">
              <Suspense fallback={
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
                </div>
              }>
                <Canvas camera={{ position: [0, 0, 4], fov: 50 }}>
                  <HeroScene3D level={state?.level || 1} xp={state?.xp || 0} />
                </Canvas>
              </Suspense>
            </div>
            
            {/* XP Progress */}
            <div className="flex-1 p-6">
              <XPProgressBar 
                current={xpProgress.xp_progress_in_level || 0}
                max={xpProgress.xp_needed_for_level || 100}
                level={state?.level || 1}
              />
              
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-2xl font-bold text-green-400">{state?.total_deliveries || 0}</p>
                  <p className="text-xs text-slate-400">Deliveries</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-400">{state?.xp?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-400">Total XP</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">{earnedBadges.length}</p>
                  <p className="text-xs text-slate-400">Badges</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Streak Section */}
        <StreakDisplay 
          days={state?.streak_days || 0}
          isActive={state?.streak_days > 0}
        />

        {/* Referral Section */}
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-green-400" />
                Refer Other Drivers
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Earn J$500 + 500 XP when they complete 5 deliveries
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="px-3 py-2 bg-slate-800 rounded-lg text-green-400 font-mono">
                {referralCode}
              </code>
              <Button size="sm" variant="outline" onClick={copyReferralCode}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Badges Section */}
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Medal className="w-6 h-6 text-yellow-500" />
            Badges
            <span className="text-sm text-slate-400 font-normal">
              ({earnedBadges.length}/{badges.length} earned)
            </span>
          </h2>
          
          {/* Earned Badges */}
          {earnedBadges.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm text-green-400 mb-3 font-medium">🏆 Earned</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {earnedBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            </div>
          )}
          
          {/* Locked Badges */}
          {unearnedBadges.length > 0 && (
            <div>
              <h3 className="text-sm text-slate-400 mb-3 font-medium">🔒 Locked</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {unearnedBadges.map(badge => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent Rewards */}
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            Recent Rewards
          </h2>
          
          {rewards.length > 0 ? (
            <div className="space-y-2">
              {rewards.map(reward => (
                <RewardItem key={reward.id} reward={reward} />
              ))}
            </div>
          ) : (
            <Card className="bg-slate-800/50 border-slate-700 p-8 text-center">
              <Gift className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">Complete deliveries to earn rewards!</p>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
